import React, { useCallback, useEffect, useState } from 'react';
import { FaPhoneAlt } from 'react-icons/fa';
import Btn from '../../../components/ui/Btn';
import StatusTag from '../../../components/ui/StatusTag';
import ConfirmModal from '../../../components/ui/ConfirmModal';
import { engineService } from '../../../services/engineService';
import { friendlyError } from '../../../services/agentsService';

// Llamadas de WhatsApp de un número ya conectado. Cada cliente conecta con su propio portafolio de Meta y Meta solo permite llamadas
// a partir de cierto nivel de mensajes: aquí se revisa el número y se dice, sin rodeos, si el canal de voz es accesible y, si no, por qué.
// El motor decide leyendo a Meta (GET) y Meta decide al activar (POST): esta pantalla solo cuenta lo que ellos responden.

const STATE = {
  enabled: { tone: 'live', label: 'Activas' },
  available: { tone: 'neutral', label: 'Disponibles' },
  unavailable: { tone: 'caution', label: 'No disponibles' },
  unknown: { tone: 'neutral', label: 'Sin confirmar' },
};
const money = (n) => Number(n).toLocaleString('es-MX');
const when = (iso) => (iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '');

export default function VoiceChannel({ conn, readOnly, engineReady }) {
  const [d, setD] = useState(null);
  const [phase, setPhase] = useState('loading');   // loading | ready | error
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');             // '' | 'check' | 'off'
  const [confirm, setConfirm] = useState(false);

  const check = useCallback(async (manual = false) => {
    if (manual) setBusy('check'); else setPhase('loading');
    setError('');
    try { setD(await engineService.voiceStatus(conn.id)); setPhase('ready'); }
    catch (e) { setError(friendlyError(e)); setPhase((p) => (manual && p === 'ready' ? 'ready' : 'error')); }
    finally { setBusy(''); }
  }, [conn.id]);

  useEffect(() => { if (engineReady) check(); else setPhase('error'); }, [engineReady, check]);

  // Si Meta no deja activar, el motor manda el diagnóstico con la explicación: se muestra aquí y no como un error suelto.
  const enable = async () => {
    try { setD(await engineService.voiceSet(conn.id, true)); setPhase('ready'); }
    catch (e) { if (e.body?.diagnosis) { setD(e.body.diagnosis); setPhase('ready'); return; } throw e; }
  };
  const disable = async () => {
    setBusy('off'); setError('');
    try { setD(await engineService.voiceSet(conn.id, false)); } catch (e) { setError(friendlyError(e)); } finally { setBusy(''); }
  };

  const st = d ? STATE[d.state] || STATE.unknown : null;
  const blocking = (d?.reasons || []).filter((r) => r.blocking);
  const others = (d?.reasons || []).filter((r) => !r.blocking);
  const tierText = d?.tier?.unlimited ? 'sin tope' : d?.tier?.limit != null ? `${money(d.tier.limit)} mensajes por día` : null;
  const deaf = d && !d.answering;

  return (
    <section aria-label="Llamadas de WhatsApp" className="my-3 rounded-xl bg-raised/50 ring-1 ring-line p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <FaPhoneAlt aria-hidden className="text-[13px] text-fg-dim" />
        <h4 className="text-[14px] font-semibold text-fg">Llamadas por WhatsApp</h4>
        {st && <StatusTag tone={st.tone} dot>{st.label}</StatusTag>}
        {d?.checked_at && <span className="ml-auto text-[12px] text-fg-faint">Revisado: {when(d.checked_at)}</span>}
      </div>

      <div role="status" aria-live="polite" className="mt-3 text-[13px] text-fg-dim leading-relaxed">
        {phase === 'loading' && <p aria-busy="true">Revisando con Meta si este número puede recibir llamadas…</p>}

        {phase === 'error' && (
          <div className="space-y-2">
            <p className="text-signal">{engineReady ? error || 'No se pudo revisar las llamadas.' : 'Falta la dirección del motor en el admin para revisar las llamadas.'}</p>
            {engineReady && <Btn size="sm" variant="quiet" onClick={() => check()}>Volver a revisar</Btn>}
          </div>
        )}

        {phase === 'ready' && d && (
          <div className="space-y-3">
            {d.state === 'enabled' && <p className="text-fg">Llamadas activas: quien escribe a este número ve el botón de llamar en WhatsApp.</p>}
            {d.state === 'available' && <p className="text-fg">Este número cumple lo que Meta pide para recibir llamadas{tierText ? ` (puede enviar hasta ${tierText})` : ''}. Todavía no están activas.</p>}
            {d.state === 'unavailable' && <p className="text-fg font-semibold">El canal de voz no está disponible para este número.</p>}
            {d.state === 'unknown' && <p className="text-fg">No se pudo confirmar si este número puede recibir llamadas.</p>}

            {blocking.length > 0 && (
              <ul className="space-y-3" aria-label="Por qué no está disponible">
                {blocking.map((r) => <Reason key={r.code} r={r} tone="caution" />)}
              </ul>
            )}
            {others.length > 0 && (
              <ul className="space-y-3" aria-label="Avisos">
                {others.map((r) => <Reason key={r.code} r={r} />)}
              </ul>
            )}

            {deaf && (d.state === 'enabled' || d.state === 'available') && (
              <p className="rounded-lg border border-caution/30 bg-caution-soft px-3 py-2 text-fg">
                <b>Por ahora el agente no contesta con voz:</b> si activas las llamadas, cada una se rechaza al instante. Actívalas solo para pruebas hasta que la voz esté lista.
              </p>
            )}

            {error && <p role="alert" className="text-signal">{error}</p>}

            <div className="flex flex-wrap items-center gap-2 pt-1">
              {d.state === 'enabled' && <Btn size="sm" variant="quiet" onClick={disable} loading={busy === 'off'} disabled={readOnly}>Desactivar llamadas</Btn>}
              {d.state === 'available' && <Btn size="sm" variant="quiet" onClick={() => setConfirm(true)} disabled={readOnly}>Activar llamadas</Btn>}
              {(d.state === 'unavailable' || d.state === 'unknown') && !readOnly && (
                <Btn size="sm" variant="ghost" onClick={() => setConfirm(true)}>{d.state === 'unknown' ? 'Intentar activar' : 'Intentar activar de todos modos'}</Btn>
              )}
              <Btn size="sm" variant={d.state === 'unavailable' || d.state === 'unknown' ? 'quiet' : 'ghost'} onClick={() => check(true)} loading={busy === 'check'}>Volver a revisar</Btn>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal open={confirm} onClose={() => setConfirm(false)} title="Activar las llamadas de este número" confirmLabel="Activar llamadas" onConfirm={enable}>
        <p>Meta mostrará el botón de llamar a quien abra el chat de este número, y avisará si no las permite.</p>
        {deaf && <p><b>Todavía no hay voz:</b> el motor rechazará cada llamada. Hazlo solo para probar.</p>}
      </ConfirmModal>
    </section>
  );
}

function Reason({ r, tone }) {
  return (
    <li className={`border-l-2 pl-3 ${tone === 'caution' ? 'border-caution' : 'border-line'}`}>
      <p className="font-semibold text-fg">{r.title}</p>
      <p className="mt-0.5">{r.detail}</p>
      {r.fix && <p className="mt-1"><span className="font-semibold text-fg">Qué hacer: </span>{r.fix}</p>}
    </li>
  );
}
